import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Users,
  FileText,
  CurrencyDollar,
  Clock,
  ChartLineUp,
  ArrowRight,
  CircleNotch,
  Warning,
  UserPlus,
  PencilLine,
  ShieldCheck,
  PaperPlaneTilt,
  Signature,
  Wrench,
  CheckCircle,
  Receipt,
  Coins,
  Archive,
  TrendUp,
  Database,
  HardDrive
} from '@phosphor-icons/react';


function StatCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon size={24} weight="bold" className="text-white" />
        </div>
      </div>
    </div>
  );
}

function QuoteStatusBadge({ status }) {
  const styles = {
    'Draft': 'badge-draft',
    'Pending Approval': 'badge-pending',
    'Approved': 'badge-approved',
    'Sent': 'badge-sent',
    'Signed': 'badge-signed',
    'Paid': 'badge-paid',
    'Expired': 'badge-expired',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'badge-draft'}`}>
      {status}
    </span>
  );
}

function ClientStatusBadge({ status }) {
  const styles = {
    'Lead': 'badge-lead',
    'Prospect': 'badge-prospect',
    'Client': 'badge-client',
    'Inactive': 'badge-inactive',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'badge-lead'}`}>
      {status}
    </span>
  );
}

const PIPELINE_STAGES = [
  { key: 'leads', label: 'Leads', icon: UserPlus, color: 'bg-sky-500', textColor: 'text-sky-600', lightBg: 'bg-sky-50', border: 'border-sky-200', link: '/leads' },
  { key: 'cotizacion', label: 'Cotizacion', icon: PencilLine, color: 'bg-amber-500', textColor: 'text-amber-600', lightBg: 'bg-amber-50', border: 'border-amber-200', link: '/quotes' },
  { key: 'aprobada', label: 'Aprobada', icon: ShieldCheck, color: 'bg-emerald-500', textColor: 'text-emerald-600', lightBg: 'bg-emerald-50', border: 'border-emerald-200', link: '/quotes' },
  { key: 'enviada', label: 'Enviada', icon: PaperPlaneTilt, color: 'bg-blue-500', textColor: 'text-blue-600', lightBg: 'bg-blue-50', border: 'border-blue-200', link: '/quotes' },
  { key: 'firmada', label: 'Firmada', icon: Signature, color: 'bg-violet-500', textColor: 'text-violet-600', lightBg: 'bg-violet-50', border: 'border-violet-200', link: '/quotes' },
  { key: 'en_progreso', label: 'En Progreso', icon: Wrench, color: 'bg-orange-500', textColor: 'text-orange-600', lightBg: 'bg-orange-50', border: 'border-orange-200', link: '/work-orders' },
  { key: 'completado', label: 'Completado', icon: CheckCircle, color: 'bg-teal-500', textColor: 'text-teal-600', lightBg: 'bg-teal-50', border: 'border-teal-200', link: '/work-orders' },
  { key: 'facturado', label: 'Facturado', icon: Receipt, color: 'bg-indigo-500', textColor: 'text-indigo-600', lightBg: 'bg-indigo-50', border: 'border-indigo-200', link: '/invoices' },
  { key: 'pagado', label: 'Pagado', icon: Coins, color: 'bg-green-600', textColor: 'text-green-600', lightBg: 'bg-green-50', border: 'border-green-200', link: '/invoices' },
  { key: 'archivado', label: 'Archivado', icon: Archive, color: 'bg-slate-500', textColor: 'text-slate-600', lightBg: 'bg-slate-100', border: 'border-slate-300', link: '/work-orders' },
];

function PipelineTracker({ pipeline }) {
  if (!pipeline) return null;
  const total = Object.values(pipeline).reduce((sum, v) => typeof v === 'number' ? sum + v : sum, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="pipeline-tracker">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-slate-900">Pipeline del Proyecto</h3>
        <Link to="/pipeline" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
          Ver Kanban <ArrowRight size={14} />
        </Link>
      </div>
      <div className="flex items-stretch gap-1 overflow-x-auto pb-2">
        {PIPELINE_STAGES.map((stage, idx) => {
          const count = pipeline[stage.key] || 0;
          const Icon = stage.icon;
          return (
            <Link
              key={stage.key}
              to={stage.link}
              className={`flex-1 min-w-[90px] relative group rounded-lg border ${stage.border} ${stage.lightBg} p-3 hover:shadow-md transition-all cursor-pointer`}
              data-testid={`pipeline-stage-${stage.key}`}
            >
              <div className="flex flex-col items-center text-center">
                <div className={`w-9 h-9 rounded-lg ${stage.color} flex items-center justify-center mb-2`}>
                  <Icon size={18} weight="bold" className="text-white" />
                </div>
                <p className={`text-2xl font-bold ${stage.textColor}`}>{count}</p>
                <p className="text-[10px] font-medium text-slate-500 leading-tight mt-0.5">{stage.label}</p>
              </div>
              {idx < PIPELINE_STAGES.length - 1 && (
                <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 z-10 text-slate-300">
                  <ArrowRight size={12} weight="bold" />
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function WeeklyTrends({ data }) {
  const [activeMetric, setActiveMetric] = useState('leads');
  if (!data || !data.weeks || data.weeks.length === 0) return null;

  const weeks = data.weeks;
  const metrics = [
    { key: 'leads', label: 'Leads', color: '#3B82F6', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-600' },
    { key: 'cotizaciones', label: 'Cotizaciones', color: '#F59E0B', bgColor: 'bg-amber-50', borderColor: 'border-amber-200', textColor: 'text-amber-600' },
    { key: 'firmadas', label: 'Firmadas', color: '#8B5CF6', bgColor: 'bg-violet-50', borderColor: 'border-violet-200', textColor: 'text-violet-600' },
    { key: 'completados', label: 'Completados', color: '#10B981', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200', textColor: 'text-emerald-600' },
    { key: 'revenue', label: 'Ingresos ($)', color: '#059669', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-700' },
  ];

  const activeDef = metrics.find(m => m.key === activeMetric);
  const values = weeks.map(w => w[activeMetric] || 0);
  const maxVal = Math.max(...values, 1);

  // SVG dimensions
  const svgW = 640;
  const svgH = 200;
  const padL = 45;
  const padR = 15;
  const padT = 15;
  const padB = 30;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;

  const points = values.map((v, i) => {
    const x = padL + (i / (values.length - 1)) * chartW;
    const y = padT + chartH - (v / maxVal) * chartH;
    return { x, y, v };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padT + chartH} L ${points[0].x} ${padT + chartH} Z`;

  // Y-axis grid
  const gridLines = 4;
  const yLabels = Array.from({ length: gridLines + 1 }, (_, i) => {
    const val = (maxVal / gridLines) * i;
    return { y: padT + chartH - (i / gridLines) * chartH, label: activeMetric === 'revenue' ? `$${Math.round(val / 1000)}k` : Math.round(val) };
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="weekly-trends">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Tendencia Semanal</h3>
          <p className="text-sm text-slate-500">Ultimas 8 semanas</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {metrics.map(m => (
            <button
              key={m.key}
              onClick={() => setActiveMetric(m.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                activeMetric === m.key
                  ? `${m.bgColor} ${m.borderColor} ${m.textColor}`
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
              data-testid={`trend-btn-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {data.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <p className="text-xs text-blue-600 font-medium">Total Leads</p>
            <p className="text-xl font-bold text-blue-700">{data.summary.total_leads}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
            <p className="text-xs text-amber-600 font-medium">Cotizaciones</p>
            <p className="text-xl font-bold text-amber-700">{data.summary.total_quotes}</p>
          </div>
          <div className="bg-violet-50 rounded-lg p-3 border border-violet-100">
            <p className="text-xs text-violet-600 font-medium">Firmadas</p>
            <p className="text-xl font-bold text-violet-700">{data.summary.total_signed}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
            <p className="text-xs text-emerald-600 font-medium">Tasa Conversion</p>
            <p className="text-xl font-bold text-emerald-700">{data.summary.conversion_rate}%</p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full min-w-[500px]" data-testid="trend-chart-svg">
          {/* Grid lines */}
          {yLabels.map((g, i) => (
            <g key={i}>
              <line x1={padL} y1={g.y} x2={svgW - padR} y2={g.y} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray={i > 0 ? "4 3" : "0"} />
              <text x={padL - 6} y={g.y + 3} textAnchor="end" fontSize="9" fill="#94A3B8">{g.label}</text>
            </g>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill={activeDef.color} opacity="0.08" />

          {/* Line */}
          <path d={linePath} fill="none" stroke={activeDef.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Data points & labels */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4" fill="white" stroke={activeDef.color} strokeWidth="2" />
              <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="9" fontWeight="600" fill={activeDef.color}>
                {activeMetric === 'revenue' ? `$${p.v.toLocaleString()}` : p.v}
              </text>
              {/* Week label */}
              <text x={p.x} y={svgH - 5} textAnchor="middle" fontSize="8" fill="#94A3B8">
                {weeks[i].week}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* Weekly close rate bar */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-xs font-semibold text-slate-500 mb-2">Tasa de Cierre Semanal</p>
        <div className="flex items-end gap-1.5 h-10">
          {weeks.map((w, i) => {
            const rate = w.tasa_cierre || 0;
            const height = Math.max(rate, 3);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                <span className="text-[9px] font-semibold text-slate-500">{rate}%</span>
                <div
                  className="w-full rounded-t"
                  style={{ height: `${height * 0.3}px`, backgroundColor: rate >= 50 ? '#10B981' : rate >= 25 ? '#F59E0B' : '#EF4444', minHeight: '2px' }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ForecastWidget() {
    const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/forecast`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {});
  }, [API_URL]);

  if (!data) return null;

  const items = [
    { label: 'En Pipeline', value: data.pipeline?.value || 0, count: data.pipeline?.count || 0, prob: '~40%', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    { label: 'Firmadas', value: data.signed?.value || 0, count: data.signed?.count || 0, prob: '~85%', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
    { label: 'Facturas Pendientes', value: data.pending_invoices?.value || 0, count: data.pending_invoices?.count || 0, prob: 'Cobro', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="forecast-widget">
      <div className="flex items-center gap-2 mb-4">
        <TrendUp size={22} className="text-emerald-600" />
        <h3 className="text-lg font-semibold text-slate-900">Pronostico 90 dias</h3>
      </div>
      <div className="text-center mb-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
        <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Ingreso Proyectado</p>
        <p className="text-3xl font-bold text-emerald-700 mt-1">${(data.projected_90d || 0).toLocaleString()}</p>
      </div>
      <div className="space-y-2.5">
        {items.map((item, i) => (
          <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg ${item.bg} border ${item.border}`}>
            <div>
              <p className="text-xs font-medium text-slate-600">{item.label}</p>
              <p className="text-[10px] text-slate-400">{item.count} proyecto{item.count !== 1 ? 's' : ''} ({item.prob})</p>
            </div>
            <p className={`font-bold text-sm ${item.color}`}>${item.value.toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportButton() {
    const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!window.confirm('Enviar reporte semanal al correo del administrador?')) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/dashboard/send-weekly-report`, {}, { withCredentials: true });
      alert('Reporte semanal enviado a tu correo!');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      onClick={handleSend}
      disabled={sending}
      className="w-full flex items-center justify-between p-3 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg transition-colors disabled:opacity-50"
      data-testid="send-report-btn"
    >
      <span className="flex items-center gap-2">
        <FileText size={18} />
        {sending ? 'Enviando...' : 'Enviar Reporte Semanal'}
      </span>
      {!sending && <ArrowRight size={18} />}
    </button>
  );
}

function BackupButton() {
    const [downloading, setDownloading] = useState(false);

  const handleBackup = async () => {
    if (!window.confirm('Descargar backup completo de la base de datos?')) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/backup/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `iqes_backup_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar backup');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleBackup}
      disabled={downloading}
      className="w-full flex items-center justify-between p-3 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors disabled:opacity-50"
      data-testid="backup-btn"
    >
      <span className="flex items-center gap-2">
        <Database size={18} />
        {downloading ? 'Descargando...' : 'Descargar Backup'}
      </span>
      {!downloading && <ArrowRight size={18} />}
    </button>
  );
}

function StorageWidget() {
  const [storage, setStorage] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/storage/stats`, { withCredentials: true })
      .then(res => setStorage(res.data))
      .catch(() => {});
  }, []);

  if (!storage) return null;

  const usedMB = storage.total_used_mb;
  const limitGB = storage.plan_limit_gb;
  const pct = storage.usage_percent;
  const barColor = pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-emerald-500';

  const collLabels = {
    leads: 'Leads', clients: 'Clientes', quotes: 'Cotizaciones',
    invoices: 'Facturas', work_orders: 'Ordenes', payment_transactions: 'Pagos',
    documents: 'Documentos', email_logs: 'Emails', audit_logs: 'Auditoria',
    calendar_events: 'Calendario', inventory: 'Inventario',
    construction_projects: 'Proyectos', users: 'Usuarios'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6" data-testid="storage-widget">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2.5 bg-slate-100 rounded-lg">
          <HardDrive size={22} className="text-slate-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Almacenamiento</h3>
          <p className="text-xs text-slate-500">{usedMB.toFixed(1)} MB de {limitGB} GB utilizados</p>
        </div>
        <span className="ml-auto text-sm font-bold text-slate-700">{pct.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-3 mb-4 overflow-hidden">
        <div className={`h-3 rounded-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Base de Datos</p>
          <p className="text-sm font-bold text-slate-800">{storage.database.total_mb.toFixed(1)} MB</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Archivos</p>
          <p className="text-sm font-bold text-slate-800">{storage.files.size_mb.toFixed(1)} MB ({storage.files.count})</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500">Indices</p>
          <p className="text-sm font-bold text-slate-800">{storage.database.index_size_mb.toFixed(1)} MB</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(storage.collections || {}).map(([key, count]) => (
          <span key={key} className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
            {collLabels[key] || key}: <strong>{count}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, trendsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/trends`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTrends(trendsRes.data);
    } catch (err) {
      setError('Error al cargar estadisticas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <Warning size={24} className="text-red-500" />
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Nuevos Leads"
          value={stats?.new_leads || 0}
          icon={Users}
          color="bg-blue-600"
          subtitle="Últimos 7 días"
        />
        <StatCard
          title="Cotizaciones Pendientes"
          value={stats?.pending_approval || 0}
          icon={FileText}
          color="bg-yellow-500"
          subtitle="Por aprobar"
        />
        <StatCard
          title="Esperando Firma"
          value={stats?.awaiting_signature || 0}
          icon={Clock}
          color="bg-purple-600"
          subtitle="Enviadas al cliente"
        />
        <StatCard
          title="Pagos del Mes"
          value={`$${(stats?.payments_this_month || 0).toLocaleString()}`}
          icon={CurrencyDollar}
          color="bg-green-600"
          subtitle="Mes actual"
        />
      </div>

      {/* Pipeline Tracker */}
      <PipelineTracker pipeline={stats?.pipeline} />

      {/* Weekly Trends Chart */}
      <WeeklyTrends data={trends} />

      {/* Forecast + Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Forecast */}
        <ForecastWidget />

        {/* Summary */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Resumen</h3>
            <ChartLineUp size={24} className="text-blue-600" />
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">Total Clientes</span>
              <span className="font-bold text-xl">{stats?.total_clients || 0}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">Valor Total Cotizaciones</span>
              <span className="font-bold text-xl text-green-600">
                ${(stats?.total_quotes_value || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600">Pipeline Activo</span>
              <span className="font-bold text-xl text-violet-600">
                ${(stats?.pipeline?.in_progress_value || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-white">
          <h3 className="text-lg font-semibold mb-4">Acciones Rapidas</h3>
          <div className="space-y-2.5">
            <Link
              to="/clients/new"
              className="flex items-center justify-between p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="quick-new-client"
            >
              <span className="flex items-center gap-2"><UserPlus size={18} /> Nuevo Cliente</span>
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/quotes/new"
              className="flex items-center justify-between p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="quick-new-quote"
            >
              <span className="flex items-center gap-2"><PencilLine size={18} /> Nueva Cotizacion</span>
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/leads"
              className="flex items-center justify-between p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="quick-leads"
            >
              <span className="flex items-center gap-2"><Users size={18} /> Ver Leads</span>
              <ArrowRight size={18} />
            </Link>
            <ReportButton />
            <BackupButton />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Quotes */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Cotizaciones Recientes</h3>
            <Link to="/quotes" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todas
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {stats?.recent_quotes?.length > 0 ? (
              stats.recent_quotes.map((quote) => (
                <Link
                  key={quote.id}
                  to={`/quotes/${quote.id}`}
                  className="block px-6 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`recent-quote-${quote.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{quote.quote_number}</p>
                      <p className="text-sm text-slate-500">{quote.client_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${quote.total?.toLocaleString()}</p>
                      <QuoteStatusBadge status={quote.status} />
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-slate-500">
                No hay cotizaciones recientes
              </div>
            )}
          </div>
        </div>

        {/* Recent Clients */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Clientes Recientes</h3>
            <Link to="/clients" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todos
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {stats?.recent_clients?.length > 0 ? (
              stats.recent_clients.map((client) => (
                <Link
                  key={client.id}
                  to={`/clients/${client.id}`}
                  className="block px-6 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`recent-client-${client.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{client.name}</p>
                      <p className="text-sm text-slate-500">{client.email}</p>
                    </div>
                    <ClientStatusBadge status={client.status} />
                  </div>
                </Link>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-slate-500">
                No hay clientes recientes
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Storage Capacity Widget */}
      <StorageWidget />
    </div>
  );
}
